/**
 * ProductDashboardPresenter
 * --
 */
import React from 'react';
import { Content, Title, Row, Col, ExpandableContainer } from 'components';
import { Divider, Button, Table, Radio, Spin, Statistic } from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { stringToMoneyFormat } from 'utils';
import {
  // XAxis,
  // YAxis,
  // BarChart,
  // Bar,
  // Legend,
  // CartesianGrid,
  // ComposedChart,
  ResponsiveContainer,
  Tooltip,
  Cell,
  PieChart,
  Pie,
} from 'recharts';

const RADIAN = Math.PI / 180;
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

/**----------------------------------------
 * [Component] 총관리자: 주문대시보드 - 프레젠터
 * ----------------------------------------
 */
const ProductDashboardPresenter = ({
  loading,
  data,
  statisticsType,
  setStatisticsType,
  permission,
  currentShop,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */

  /* ===== VARIABLE ===== */
  const formItem = [
    {
      label: '기간설정',
      form: [
        {
          input: (
            <>
              <Radio.Group
                size="large"
                defaultValue="month"
                buttonStyle="solid"
                value={statisticsType}
                onChange={(e) => setStatisticsType(e.target.value)}
                style={{ marginRight: 12 }}
              >
                <Radio.Button value="today">오늘</Radio.Button>
                <Radio.Button value="week">1주일(7일)</Radio.Button>
                <Radio.Button value="month">1개월(30일)</Radio.Button>
                <Radio.Button value="3month">3개월(90일)</Radio.Button>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
  ];

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  let dd = !data ? [] : data.shop_information;
  if (currentShop && permission !== 'ADMIN') {
    dd = !data
      ? []
      : data.shop_information.filter(
          (item) => item.shop_information_id === currentShop
        );
  }

  // 전제 상품
  const totalProductCount = !dd
    ? 0
    : dd.reduce((acc, cur) => acc + Number(cur.product_count), 0);
  // 판매중인 상품
  const totalSalesCount = !dd
    ? 0
    : dd.reduce((acc, cur) => acc + Number(cur.sales_product_count), 0);
  // 심사완료 상품
  const totalAuditCount = !dd
    ? 0
    : dd.reduce((acc, cur) => acc + Number(cur.audit_product_count), 0);
  // 심사대기 상품
  const totalAuditWaitCount = !dd
    ? 0
    : dd.reduce((acc, cur) => acc + Number(cur.audit_wait_product_count), 0);
  // 심사대기 상품
  const totalSoldoutCount = !dd
    ? 0
    : dd.reduce((acc, cur) => acc + Number(cur.soldout_product_count), 0);

  /* ===== RENDER ===== */
  // console.log('data: ', data);

  // JSX: start
  return (
    <Spin spinning={loading}>
      <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
        <Row>
          <Col x={24}>
            <Title size={3} style={{ marginBottom: 12 }}>
              상품 대시보드
            </Title>

            {/* === 현황 요약: start === */}
            <Content
              fullWidth
              padding={0}
              backgroundColor={'none'}
              style={{
                marginBottom: 5,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {[
                {
                  title: '전체상품',
                  value: stringToMoneyFormat(totalProductCount),
                  suffix: '개',
                  link: '/product/list',
                },
                {
                  title: '판매중인 상품(심사완료)',
                  value: stringToMoneyFormat(totalSalesCount),
                  suffix: '개',
                  link: null, // '/product/list',
                },
                {
                  title: '품절상품',
                  value: stringToMoneyFormat(totalSoldoutCount),
                  suffix: '개',
                  link: null, // '/product/list',
                },
                {
                  title: '심사된 상품',
                  value: stringToMoneyFormat(totalAuditCount),
                  suffix: '개',
                  link: null, // '/product/list',
                },
                {
                  title: '심사대기상품',
                  value: stringToMoneyFormat(totalAuditWaitCount),
                  suffix: '개',
                  link: '/product-audit/list',
                },
              ].map((_item, _index) => (
                <div
                  key={`${_item.label}-${_index}`}
                  className="boxShadow"
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 4,
                    width: `90%`,
                    margin: '0 2px',
                    padding: 24,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Statistic
                    className="cursorOverOpacity"
                    direction={'row'}
                    size="large"
                    title={_item.title}
                    value={_item.value}
                    suffix={_item.suffix}
                    // style={{ justifyContent: 'center' }}
                    valueStyle={{
                      color: '#1677ff',
                      textDecoration: 'underline !important',
                    }}
                  />
                  {_item.link && (
                    <div>
                      <Button
                        shape="circle"
                        onClick={() => window.open(_item.link, '_blank')}
                      >
                        <ExportOutlined />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </Content>
            {/* === 현황 요약: end === */}

            {/* ===== 차트: start ===== */}
            <Row gutter={[5, 5]}>
              {/* === 상품별: start === */}
              <Col x={permission === 'ADMIN' ? 10 : 12}>
                <Content
                  maxWidth={'100%'}
                  borderRadius={4}
                  // padding={'14px 5px'}
                  height={500}
                >
                  <Title size={5} style={{ marginBottom: 12 }}>
                    카테고리별 등록상품 현황
                  </Title>
                  <Divider style={{ margin: 0 }} />
                  <Table
                    bordered
                    size="small"
                    scroll={{ x: 'max-content', y: 450 }}
                    pagination={false}
                    dataSource={data?.category ?? []}
                    columns={[
                      {
                        title: '카테고리명',
                        dataIndex: 'product_category_name',
                        align: 'center',
                      },
                      {
                        title: '상품수',
                        dataIndex: 'product_count',
                        width: 150,
                        align: 'center',
                        render: (value) =>
                          `${stringToMoneyFormat(Number(value))}`,
                      },
                    ]}
                  />
                </Content>
              </Col>
              {/* === 상품별: end === */}

              {/* === 내역: start === */}
              <Col x={permission === 'ADMIN' ? 14 : 12}>
                <Content
                  maxWidth={'100%'}
                  height={500}
                  borderRadius={4}
                  // padding={'14px 5px'}
                >
                  <Title size={5} style={{ marginBottom: 12 }}>
                    입점업체 상품현황
                  </Title>

                  {permission === 'ADMIN' ? (
                    <Table
                      bordered
                      size="small"
                      scroll={{ x: 650, y: 450 }}
                      pagination={false}
                      dataSource={data?.shop_information ?? []}
                      columns={[
                        {
                          title: '업체명',
                          dataIndex: 'shop_information_name',
                          align: 'center',
                        },
                        {
                          title: '상품수',
                          dataIndex: 'product_count',
                          width: 110,
                          align: 'center',
                          render: (value) =>
                            `${stringToMoneyFormat(Number(value))}`,
                        },
                        {
                          title: '판매중',
                          dataIndex: 'sales_product_count',
                          width: 110,
                          align: 'center',
                          render: (value) =>
                            `${stringToMoneyFormat(Number(value))}`,
                        },
                        {
                          title: '심사대기',
                          dataIndex: 'audit_wait_product_count',
                          width: 110,
                          align: 'center',
                          render: (value) =>
                            `${stringToMoneyFormat(Number(value))}`,
                        },
                        {
                          title: '품절',
                          dataIndex: 'soldout_product_count',
                          width: 110,
                          align: 'center',
                          render: (value) =>
                            `${stringToMoneyFormat(Number(value))}`,
                        },
                      ]}
                    />
                  ) : (
                    <>
                      <PieChart
                        width={590}
                        height={400}
                        style={{
                          width: '100%',
                          // width: 1200,
                          padding: 10,
                        }}
                      >
                        <Pie
                          // data={data}
                          data={data?.category?.map((item) => ({
                            ...item,
                            value: Number(item.product_count),
                          }))}
                          cx="50%"
                          cy="50%"
                          // paddingAngle={1}
                          // label
                          isAnimationActive={false}
                          // labelLine={false}
                          label={(d) => {
                            const {
                              cx,
                              cy,
                              midAngle,
                              innerRadius,
                              outerRadius,
                              percent,
                              product_category_name: name,
                            } = d;
                            // const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                            const radius =
                              innerRadius + (outerRadius - innerRadius) * 1.4;
                            const x =
                              cx + radius * Math.cos(-midAngle * RADIAN);
                            const y =
                              cy + radius * Math.sin(-midAngle * RADIAN);

                            return (
                              <text
                                x={x}
                                y={y}
                                fill="black"
                                textAnchor={x > cx ? 'start' : 'end'}
                                dominantBaseline="central"
                                // width={20}
                              >
                                {`${name}: \n${(percent * 100).toFixed(0)}%`}
                              </text>
                            );
                          }}
                          innerRadius={35}
                          outerRadius={90}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {data?.category?.slice(0, 10).map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </>
                  )}
                </Content>
              </Col>
              {/* === 내역: end === */}
            </Row>
            {/* ===== 차트: end ===== */}
          </Col>

          <Col x={24}>
            {/* <br /> */}
            <Button
              block
              size="large"
              style={{ marginTop: 5 }}
              onClick={() => window.open('/product/list', '_blank')}
            >
              상품목록 바로가기 <ExportOutlined />
            </Button>
          </Col>
        </Row>
      </Content>
    </Spin>
  ); // JSX: end
};

export default ProductDashboardPresenter;
